import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Calendar from 'react-calendar';
import { useHistoryWithHost } from 'routes/useHistoryWithHost';
import { ApplicationState } from 'store/store.inteface';
import {
  getDataTable,
  setFilterDateTable,
  setFilterStatusTable,
} from 'store/modules/management/actions';
import { statusList, movementList } from 'utils/statusList';
import { ROLES } from 'config/roles';
import {
  CardWrapper,
  CardHeader,
  SelectTypeClaims,
  SelectTypeClaimsIcon,
  CalendarRef,
  CalendarInput,
  CalendarIcon,
  CardTable,
  Loading,
} from './styles';
import { CalendarWrap } from '../../stylesCalendar';

interface IProps {
  amount: number | null;
  count: number;
  name: string;
  id: string;
}
interface IType {
  type: 'status' | 'movement';
  tableName: 'tableA' | 'tableB' | 'tableC';
}

const ClaimsByType = ({ type, tableName }: IType): JSX.Element => {
  const stateTable = useSelector(
    (state: ApplicationState) => state.management[type][tableName],
  );

  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );

  const { rolesList } = useSelector((state: ApplicationState) => state.roles);
  const { dates, status } = stateTable.filter;

  const [valueInput, setValueInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const history = useHistoryWithHost();
  const dispatch = useDispatch();

  const formatDate = (date: any) => {
    const newDate = new Date(date);
    const day = newDate.toLocaleString('pt-BR', { day: '2-digit' });
    const month = newDate.toLocaleString('pt-BR', { month: '2-digit' });
    const year = newDate.toLocaleString('pt-BR', { year: 'numeric' });
    const newDateString = `${day}/${month}/${year}`;
    return newDateString;
  };

  const formatDateSearch = (date: any) => {
    const newDate = new Date(date);
    const day = newDate.toLocaleString('pt-BR', { day: '2-digit' });
    const month = newDate.toLocaleString('pt-BR', { month: '2-digit' });
    const year = newDate.toLocaleString('pt-BR', { year: 'numeric' });
    const newDateString = `${year}-${month}-${day}`;
    return newDateString;
  };

  const onChangeCalendar = (date: any) => {
    dispatch(setFilterDateTable(tableName, type, date));
  };

  const ref = useRef<any>();

  const changeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setFilterStatusTable(tableName, type, e.target.value));
  };

  const linkToSearchClaims = (id: string) => {
    history.push(
      `/claim?id=${id}&startDate=${formatDateSearch(
        dates[0],
      )}&endDate=${formatDateSearch(dates[1])}&status=${status}`,
    );
  };

  const dispatchGetDataTable = () => {
    let searchValue: any = '';
    if (type === 'status') {
      searchValue = statusList.find(x => x.value === status)?.searchValue;
    } else {
      searchValue = status;
    }
    dispatch(
      getDataTable(tableName, type, {
        status: searchValue,
        startDate: formatDateSearch(dates[0]),
        endDate: formatDateSearch(dates[1]),
      }),
    );
  };

  useEffect(() => {
    setLoading(true);
    if (stores.length > 0 || rolesList[0] === ROLES.MANAGEMENT) {
      dispatchGetDataTable();
    }
  }, [dates, status, stores, rolesList]);

  useEffect(() => {
    if (stateTable.data) {
      setLoading(false);
    }
  }, [stateTable.data]);

  useEffect(() => {
    setValueInput(`${formatDate(dates[0])} à ${formatDate(dates[1])}`);
  }, [dates]);

  useEffect(() => {
    const listener = (event: TouchEvent | MouseEvent) => {
      if (!ref.current || ref.current?.contains(event.target)) {
        return;
      }
      setShowCalendar(false);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref]);

  return (
    <CardWrapper>
      <CardHeader>
        {type === 'movement' && (
          <SelectTypeClaims value={status} onChange={e => changeType(e)}>
            {movementList.map((item: any) => (
              <option value={item.value} key={item.label}>
                {item.label}
              </option>
            ))}
          </SelectTypeClaims>
        )}
        {type === 'status' && (
          <SelectTypeClaims value={status} onChange={e => changeType(e)}>
            {statusList.map((item: any) => (
              <option value={item.value} key={item.label}>
                {item.label}
              </option>
            ))}
          </SelectTypeClaims>
        )}
        <SelectTypeClaimsIcon />
        <CalendarRef ref={ref}>
          <CalendarInput onClick={() => setShowCalendar(!showCalendar)}>
            <span>Período:</span>
            <input
              id="inputDate"
              value={valueInput}
              onChange={() => null}
              disabled
            />
            <CalendarIcon />
          </CalendarInput>

          {showCalendar && (
            <CalendarWrap>
              <Calendar
                onChange={(date: any) => onChangeCalendar(date)}
                value={dates}
                selectRange
              />
            </CalendarWrap>
          )}
        </CalendarRef>
      </CardHeader>

      {loading && <Loading />}
      {!loading && stateTable.data && (
        <CardTable type={stateTable?.data?.amount_title}>
          <div className="card-header">
            <div>Cobertura</div>
            <div>Qnt.</div>
            <div>{stateTable?.data?.amount_title}</div>
          </div>
          <div
            className="card-row-total"
            onClick={() => linkToSearchClaims('')}
            onKeyDown={() => linkToSearchClaims('')}
            role="button"
            tabIndex={0}
          >
            <div>TOTAL</div>
            <div>{stateTable?.data?.total?.count}</div>
            <div>
              {stateTable?.data?.total?.amount?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          {stateTable?.data?.coverages?.map((item: IProps) => {
            const { amount, count, name, id } = item;
            return (
              <div
                className="card-row"
                key={`${id}`}
                onClick={() => linkToSearchClaims(id)}
                onKeyDown={() => linkToSearchClaims(id)}
                role="button"
                tabIndex={0}
              >
                <div>{name}</div>
                <div>{count}</div>
                <div>
                  {amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            );
          })}
        </CardTable>
      )}
    </CardWrapper>
  );
};

export default ClaimsByType;
